import { useState } from "preact/hooks";
import { PencilSquareIcon, PlusIcon } from "@heroicons/react/24/outline";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Field,
  Input,
  SectionHeading,
  SkeletonList,
} from "../ui";
import { useConfirm } from "../../lib/use-confirm";
import { adminHref } from "../../lib/platform-admin";
import type { ContentSummaryDto } from "@shared/types";

type Props = {
  projectId: string;
  contents: ContentSummaryDto[] | null;
};

export function ContentListSection({ projectId, contents }: Props) {
  const [newSlug, setNewSlug] = useState("");
  const { confirm, dialog: confirmDialog } = useConfirm();

  async function onStartNew(e: Event) {
    e.preventDefault();
    if (!newSlug) return;
    // Skip the collision check while the list is still loading — the editor
    // page will still load the right document either way, and we don't want
    // to block the user just because the list hasn't arrived yet.
    if (contents?.some((c) => c.slug === newSlug)) {
      const ok = await confirm({
        title: `/${newSlug} は既に存在します`,
        description: "編集画面を開きますか？",
        confirmLabel: "開く",
        cancelLabel: "キャンセル",
      });
      if (!ok) return;
    }
    location.href = adminHref(
      `/projects/${projectId}/contents/${encodeURIComponent(newSlug)}`,
    );
  }

  return (
    <div class="space-y-6">
      {confirmDialog}
      <SectionHeading level={3} title="コンテンツ" />

      <Card>
        <CardBody>
          <form onSubmit={onStartNew} class="flex flex-wrap items-end gap-3">
            <Field label="新規 slug" class="min-w-[220px] flex-1">
              <Input
                value={newSlug}
                onInput={(e) =>
                  setNewSlug((e.target as HTMLInputElement).value)
                }
                required
                pattern="[a-z0-9](?:[a-z0-9\-]*[a-z0-9])?"
                placeholder="pricing"
              />
            </Field>
            <Button
              type="submit"
              variant="primary"
              iconLeft={<PlusIcon class="h-4 w-4" />}
            >
              エディタを開く
            </Button>
          </form>
        </CardBody>
      </Card>

      {contents === null ? (
        <SkeletonList count={3} />
      ) : contents.length === 0 ? (
        <EmptyState
          icon={<PencilSquareIcon class="h-5 w-5" />}
          title="コンテンツはまだありません"
          description="上のフォームから新規 slug を入力してください。"
        />
      ) : (
        <ul class="grid gap-2">
          {contents.map((ct) => (
            <li
              key={ct.id}
              class="rounded-lg border border-border bg-bg transition hover:border-border-strong hover:shadow-xs"
            >
              <div class="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                <div class="min-w-0 space-y-1.5">
                  <div class="truncate text-sm font-medium text-fg">
                    {ct.title}
                  </div>
                  <div class="flex flex-wrap items-center gap-1.5 text-xs">
                    <span class="font-mono text-fg-subtle">/{ct.slug}</span>
                    {ct.tags.map((t) => (
                      <Badge key={t} tone="neutral">
                        #{t}
                      </Badge>
                    ))}
                  </div>
                </div>
                <Button
                  as="a"
                  href={adminHref(`/projects/${projectId}/contents/${ct.slug}`)}
                  size="sm"
                  variant="secondary"
                  iconLeft={<PencilSquareIcon class="h-3.5 w-3.5" />}
                >
                  編集
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
